import React, { useEffect, useState } from "react";
import "./ChatPage.css";
import useChat from "../../hooks/useChat";
import { authService } from "../../service";
import { FemaleAvatar, InvestAvatar } from "../../assets";
import { toast } from "react-toastify";
import { FaPaperPlane } from "react-icons/fa";

const ChatPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); 

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await authService.getMe();
        setCurrentUser(data);
      } catch (err) {
        console.error("Erreur lors du chargement de l'utilisateur :", err);
        toast.error("Impossible de récupérer votre profil.");
      }
    };
    fetchUser();
  }, []);

  const {
    users,
    activeUser,
    setActiveUser,
    messages,
    sendMessage,
    messageInputRef,
    onlineUsers,
  } = useChat(currentUser?.id);

  // --- Filter users based on search term ---
  const filteredUsers = users.filter((user) => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  // --- Message Bubble component ---
  const MessageBubble = ({ content, isSender, time, gender }) => {
    const avatarSrc =
      gender?.toLowerCase() === "femme" ? FemaleAvatar : InvestAvatar;

    return (
      <div
        className={`message-wrapper d-flex flex-column ${
          isSender ? "align-items-end" : "align-items-start"
        } mb-3`}
      >
        <div
          className={`d-flex ${
            isSender ? "justify-content-end" : "justify-content-start"
          }`}
        >
          {!isSender && (
            <img
              src={avatarSrc}
              alt="Avatar"
              className="chat-avatar me-2 mt-auto"
              style={{ width: "34px", height: "34px" }}
            />
          )}

          <div
            className={`message-bubble text-wrap p-3 shadow-sm ${
              isSender ? "sender-bubble" : "receiver-bubble"
            }`}
          >
            <p className="mb-0 fw-normal">{content}</p>
          </div>

          {isSender && (
            <img
              src={avatarSrc}
              alt="User Avatar"
              className="chat-avatar ms-2 mt-auto"
              style={{ width: "34px", height: "34px" }}
            />
          )}
        </div>

        {/* Time below bubble */}
        <small
          className="text-muted mt-1 me-5 ms-5"
          style={{
            fontSize: "0.75rem",
            color: "var(--textsecondary)",
          }}
        >
          {new Date(time).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC",
          })}
        </small>
      </div>
    );
  };

  return (
    <main className="content flex-grow-1">
      <div className="container-fluid h-100 p-3">
        <div className="chat-page-inner d-flex h-100 bg-white">
          {/* Sidebar */}
          <div className="chat-sidebar col-4 d-flex flex-column rounded-start-4">
            <div className="sidebar-header d-flex justify-content-between align-items-center p-4 border-bottom">
              <h5 className="fw-semibold m-0 text-dark">Messagerie</h5>
            </div>

            {/* Search input */}
            <div className="p-3 border-bottom bg-light d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control sidebar-search-input ps-4"
                placeholder="Rechercher un contact..."
                style={{
                  borderRadius: "20px",
                  border: "1px solid var(--greenprimary)",
                  paddingLeft: "30px",
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* User list */}
            <div className="chat-list flex-grow-1 overflow-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-muted mt-4">
                  Aucun contact trouvé.
                </p>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`chat-user-item d-flex align-items-center p-3 ${
                      activeUser?.id === user.id ? "active-chat-item" : ""
                    }`}
                    onClick={() => setActiveUser(user)}
                  >
                    <img
                      src={
                        user.gender?.toLowerCase() === "femme"
                          ? FemaleAvatar
                          : InvestAvatar
                      }
                      className="chat-list-avatar me-3 rounded-circle"
                      alt={user.first_name}
                      style={{
                        width: "38px",
                        height: "38px",
                        border: "2px solid var(--backgroundtertiary)",
                      }}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                        {user.first_name} {user.last_name}
                        {onlineUsers.includes(user.id) && (
                          <span className="status-dot bg-success"></span>
                        )}
                      </div>
                      <small className="text-muted">
                        {activeUser?.id === user.id ? "Chat actif" : ""}
                      </small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Window */}
          <div className="chat-window col-8 d-flex flex-column rounded-end-4 border-start">
            {activeUser ? (
              <>
                <div className="chat-header d-flex align-items-center p-3 border-bottom">
                  <img
                    src={
                      activeUser.gender?.toLowerCase() === "femme"
                        ? FemaleAvatar
                        : InvestAvatar
                    }
                    alt="Avatar"
                    className="rounded-circle me-2"
                    style={{ width: "40px", height: "40px" }}
                  />
                  <h6 className="fw-semibold m-0">
                    {activeUser.first_name} {activeUser.last_name}
                  </h6>
                </div>

                <div className="message-area flex-grow-1 p-4 overflow-auto bg-light">
                  {messages.length === 0 ? (
                    <p className="text-muted text-center mt-5">
                      Aucun message ici.
                    </p>
                  ) : (
                    messages.map((msg, idx) => (
                      <MessageBubble
                        key={idx}
                        gender={
                          msg.sender_id === currentUser?.id
                            ? currentUser?.gender
                            : activeUser?.gender
                        }
                        content={msg.content}
                        isSender={msg.sender_id === currentUser?.id}
                        time={msg.timestamp || msg.created_at}
                      />
                    ))
                  )}
                </div>

                {/* Input with send button */}
                <div className="input-group">
                  <input
                    ref={messageInputRef}
                    type="text"
                    className="form-control chat-input"
                    placeholder="Taper un message..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        sendMessage(messageInputRef.current.value);
                      }
                    }}
                  />
                  <button
                    className="btn send-button"
                    type="button"
                    onClick={() => sendMessage(messageInputRef.current.value)}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-center align-items-center flex-grow-1">
                <p className="text-muted">Sélectionnez une conversation</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ChatPage;
